import { useCallback, useEffect, useRef, useState } from 'react';
import { usePdfRenderer } from '../../hooks/usePdfRenderer';
import * as api from '../../api/endpoints';

interface CropModalProps {
  filename: string;
  page: number;
  onCapture: (base64: string) => void;
  onClose: () => void;
  onPageChange: (page: number) => void;
}

export function CropModal({ filename, page, onCapture, onClose, onPageChange }: CropModalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { renderPage } = usePdfRenderer();

  const [drawing, setDrawing] = useState(false);
  const [startPt, setStartPt] = useState({ x: 0, y: 0 });
  const [rect, setRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [rendered, setRendered] = useState(false);
  const [pageCount, setPageCount] = useState(1);

  // Render the page
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setRendered(false);
    setRect(null);

    const url = api.pdfUrl(filename);
    const containerWidth = containerRef.current?.clientWidth || 800;

    renderPage(canvas, url, page, { containerWidth, maxCanvasSide: 4096 }).then((result) => {
      if (result) setRendered(true);
    });

    // Get page count from project store
    // We can't easily import store here, so just read from the project files API
  }, [filename, page, renderPage]);

  const getMousePos = useCallback(
    (e: React.MouseEvent): { x: number; y: number } => {
      const overlay = overlayRef.current;
      if (!overlay) return { x: 0, y: 0 };
      const rect = overlay.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    },
    [],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      const pos = getMousePos(e);
      setStartPt(pos);
      setRect(null);
      setDrawing(true);
    },
    [getMousePos],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!drawing) return;
      const pos = getMousePos(e);
      setRect({
        x: Math.min(startPt.x, pos.x),
        y: Math.min(startPt.y, pos.y),
        w: Math.abs(pos.x - startPt.x),
        h: Math.abs(pos.y - startPt.y),
      });
    },
    [drawing, startPt, getMousePos],
  );

  const handleMouseUp = useCallback(() => {
    setDrawing(false);
  }, []);

  const handleCapture = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !rect || rect.w < 5 || rect.h < 5) return;

    const dpr = window.devicePixelRatio || 1;
    // The canvas CSS size vs actual pixel size ratio
    const cssW = canvas.clientWidth;
    const scaleX = canvas.width / cssW;
    const cssH = canvas.clientHeight;
    const scaleY = canvas.height / cssH;

    const offscreen = document.createElement('canvas');
    const cropW = Math.round(rect.w * scaleX);
    const cropH = Math.round(rect.h * scaleY);
    offscreen.width = cropW;
    offscreen.height = cropH;
    const ctx = offscreen.getContext('2d')!;
    ctx.drawImage(
      canvas,
      Math.round(rect.x * scaleX),
      Math.round(rect.y * scaleY),
      cropW,
      cropH,
      0,
      0,
      cropW,
      cropH,
    );

    const dataUrl = offscreen.toDataURL('image/png');
    const base64 = dataUrl.replace('data:image/png;base64,', '');
    onCapture(base64);
  }, [rect, onCapture]);

  return (
    <div className="crop-modal-backdrop" onClick={onClose}>
      <div className="crop-modal" onClick={(e) => e.stopPropagation()}>
        <div className="crop-modal-header">
          <span className="crop-modal-title">
            Crop from: {filename} p.{page}
          </span>
          <div className="crop-modal-nav">
            <button
              className="crop-nav-btn"
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page <= 1}
            >
              &larr;
            </button>
            <span>Page {page}</span>
            <button
              className="crop-nav-btn"
              onClick={() => onPageChange(page + 1)}
            >
              &rarr;
            </button>
          </div>
          <div className="crop-modal-actions">
            <button
              className="crop-capture-btn"
              onClick={handleCapture}
              disabled={!rect || rect.w < 5 || rect.h < 5}
            >
              Capture
            </button>
            <button className="crop-close-btn" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
        <div className="crop-modal-body" ref={containerRef}>
          {!rendered && <div className="crop-loading">Loading page...</div>}
          <div className="crop-canvas-container">
            <canvas ref={canvasRef} />
            <div
              ref={overlayRef}
              className="crop-overlay"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {rect && rect.w > 2 && rect.h > 2 && (
                <div
                  className="crop-selection"
                  style={{
                    left: rect.x,
                    top: rect.y,
                    width: rect.w,
                    height: rect.h,
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
